import React from 'react';
import { useTranslation } from 'react-i18next';
import { calculateSkillCost } from '../../../Calculations/SkillCostCalculator';

import { calculateAdjustedScore } from '../../../Calculations/SkillScoreCalculator';

const SkillCostDisplay = ({ activeSkills, aptitudes }) => {
    const { t } = useTranslation();
    const { totalCost, metrics } = React.useMemo(() => {
        if (!activeSkills || activeSkills.length === 0) {
            return {
                totalCost: 0,
                metrics: { minCost: 0, maxScore: 0, avgCost: 0, avgScore: 0, count: 0 }
            };
        }

        let sumCost = 0;
        let sumScore = 0;
        let minCost = Infinity;
        let maxScore = 0;
        let activeCount = 0;

        activeSkills.forEach(item => {
            if (!item.active) return;

            // Cost Calc
            let cost = 0;
            if (!item.ignoreCost) {
                const baseCost = item.skill.cost || 0;
                cost = calculateSkillCost(baseCost, item.level, item.fastLearner);
            }

            // Score Calc
            const scoreResult = calculateAdjustedScore(item.skill, aptitudes);
            const score = scoreResult.adjustedScore || 0;

            sumCost += cost;
            sumScore += score;
            if (cost < minCost) minCost = cost;
            if (score > maxScore) maxScore = score;
            activeCount++;
        });

        if (activeCount === 0) {
            return {
                totalCost: 0,
                metrics: { minCost: 0, maxScore: 0, avgCost: 0, avgScore: 0, count: 0 }
            };
        }

        return {
            totalCost: sumCost,
            metrics: {
                minCost: minCost === Infinity ? 0 : minCost,
                maxScore: maxScore,
                avgCost: Math.round(sumCost / activeCount),
                avgScore: Math.round(sumScore / activeCount),
                count: activeCount
            }
        };

    }, [activeSkills, aptitudes]);

    return (
        <div className="bg-white rounded-2xl shadow-lg p-5 h-full flex flex-col">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2">{t('SkillCostDisplay.title')}</h3>

            {/* Total Cost Banner */}
            <div className="flex items-center justify-between bg-blue-50 p-4 rounded-xl border border-blue-100 mb-4">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                    </div>
                    <div>
                        <div className="text-sm text-gray-500 font-medium uppercase tracking-wide">{t('SkillCostDisplay.label_total_cost')}</div>
                        <div className="text-xs text-gray-400">{metrics.count} {t('SkillCostDisplay.suffix_active_skills')}</div>
                    </div>
                </div>
                <div className="text-3xl font-bold text-blue-600 font-mono">
                    {totalCost}
                </div>
            </div>

            {/* Detailed Metrics Grid */}
            <div className="grid grid-cols-2 gap-4 flex-1">
                {/* Min Cost */}
                <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 flex flex-col justify-center">
                    <span className="text-xs text-gray-400 uppercase font-bold">{t('SkillCostDisplay.label_min_cost')}</span>
                    <div className="flex items-baseline gap-1 mt-1">
                        <span className="text-xl font-bold text-gray-700">{metrics.minCost}</span>
                        <span className="text-xs text-gray-400">pt</span>
                    </div>
                </div>

                {/* Max Score */}
                <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 flex flex-col justify-center">
                    <span className="text-xs text-gray-400 uppercase font-bold">{t('SkillCostDisplay.label_max_score')}</span>
                    <div className="flex items-baseline gap-1 mt-1">
                        <span className="text-xl font-bold text-emerald-600">{metrics.maxScore}</span>
                        <span className="text-xs text-gray-400">pt</span>
                    </div>
                </div>

                {/* Avg Cost */}
                <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 flex flex-col justify-center">
                    <span className="text-xs text-gray-400 uppercase font-bold">{t('SkillCostDisplay.label_avg_cost')}</span>
                    <div className="flex items-baseline gap-1 mt-1">
                        <span className="text-xl font-bold text-blue-500">{metrics.avgCost}</span>
                        <span className="text-xs text-gray-400">pt/skill</span>
                    </div>
                </div>

                {/* Avg Score */}
                <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 flex flex-col justify-center">
                    <span className="text-xs text-gray-400 uppercase font-bold">{t('SkillCostDisplay.label_avg_score')}</span>
                    <div className="flex items-baseline gap-1 mt-1">
                        <span className="text-xl font-bold text-purple-500">{metrics.avgScore}</span>
                        <span className="text-xs text-gray-400">pt/skill</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SkillCostDisplay;
// Revisado: 2025-12-18 [I18n Scan]
